import { type Agent } from "@src/appV2/User/api/useSearchAgents";
import { type Facility } from "@src/appV2/User/api/useSearchFacilities";
import { useCallback } from "react";

import { type WorkerShift } from "../api/useGetWorkerShifts";
import { type WorkerStripeAccount } from "../api/useGetWorkerStripeAccount";
import { type WorkplaceShift } from "../api/useGetWorkplaceShifts";
import { type ShiftSearchResult } from "../api/useSearchShift";
import { type ShiftAction } from "../components/ShiftActionItem";
import { type AgentImpersonateData } from "../types";
import { type SearchResultItem } from "./useOmnisearchResults";

interface UseOmnisearchSelectParams {
  onSelectAgent: (agent: Agent) => void;
  onSelectFacility: (facility: Facility) => void;
  onSelectShift: (shift: ShiftSearchResult) => void;
  onSelectShiftAction: (shift: ShiftSearchResult, action: ShiftAction) => void;
  onSelectAgentShift: (workerShift: WorkerShift) => void;
  onSelectAgentStripe: (stripeAccount: WorkerStripeAccount) => void;
  onSelectAgentImpersonate: (data: AgentImpersonateData) => boolean;
  onSelectWorkplaceShift: (workplaceShift: WorkplaceShift) => void;
  onLoadMoreAgents: () => void;
  onLoadMoreFacilities: () => void;
  onClose: () => void;
  resetState: () => void;
}

export function useOmnisearchSelect(params: UseOmnisearchSelectParams) {
  const {
    onSelectAgent,
    onSelectFacility,
    onSelectShift,
    onSelectShiftAction,
    onSelectAgentShift,
    onSelectAgentStripe,
    onSelectAgentImpersonate,
    onSelectWorkplaceShift,
    onLoadMoreAgents,
    onLoadMoreFacilities,
    onClose,
    resetState,
  } = params;

  return useCallback(
    (item: SearchResultItem) => {
      switch (item.type) {
        case "agent": {
          onSelectAgent(item.data);
          break;
        }
        case "facility": {
          onSelectFacility(item.data);
          break;
        }
        case "shift": {
          onSelectShift(item.data);
          break;
        }
        case "shift-action": {
          onSelectShiftAction(item.data, item.action);
          break;
        }
        case "agent-shift": {
          onSelectAgentShift(item.data);
          break;
        }
        case "agent-stripe": {
          onSelectAgentStripe(item.data);
          break;
        }
        case "agent-impersonate": {
          const result = onSelectAgentImpersonate({ agent: item.data, workerId: item.workerId });
          if (!result) {
            return;
          }
          break;
        }
        case "workplace-shift": {
          onSelectWorkplaceShift(item.data);
          break;
        }
        case "agent-load-more": {
          onLoadMoreAgents();
          return;
        }
        case "facility-load-more": {
          onLoadMoreFacilities();
          return;
        }
        default: {
          break;
        }
      }
      onClose();
      resetState();
    },
    [
      onSelectAgent,
      onSelectFacility,
      onSelectShift,
      onSelectShiftAction,
      onSelectAgentShift,
      onSelectAgentStripe,
      onSelectAgentImpersonate,
      onSelectWorkplaceShift,
      onLoadMoreAgents,
      onLoadMoreFacilities,
      onClose,
      resetState,
    ]
  );
}
